import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, ChevronDown, LogOut, Menu, Moon, RefreshCw, Sun } from 'lucide-react';
import { PAGE_TITLES } from '@/app/nav';
import { useDashboard, useLogout, useSession } from '@/lib/hooks';
import { useTema } from '@/lib/tema';

/** "Ana María Pérez" → "AP". Sin nombre, un guion. */
function iniciales(name?: string): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '—';
  return (parts[0]![0]! + (parts.length > 1 ? parts[parts.length - 1]![0]! : '')).toUpperCase();
}

export function Topbar({ onMenu }: { onMenu: () => void }) {
  const { pathname } = useLocation();
  const qc = useQueryClient();
  const title = PAGE_TITLES[pathname] ?? 'BetStock IA';
  const user = useSession().data?.user;
  const logout = useLogout();
  const [menu, setMenu] = useState(false);
  // La fecha sale del último día con venta real ingerida (la sirve /api/dashboard). Antes era
  // una constante con el "hoy" ficticio del seed, que quedaba igual pasara lo que pasara.
  const dash = useDashboard();
  const alertas = dash.data?.alertSummary.active ?? 0;
  const { tema, alternar } = useTema();

  // El título de la ruta se mudó de la barra al título del DOCUMENTO, que es donde de
  // verdad hacía falta: con varias pestañas abiertas todas decían "BetStock IA".
  useEffect(() => {
    document.title = `${title} · BetStock IA`;
  }, [title]);

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-4 border-b border-hairline bg-canvas/85 px-5 backdrop-blur">
      <div className="flex min-w-0 items-center gap-3">
        <button
          onClick={onMenu}
          className="grid h-8 w-8 place-items-center rounded-lg text-ink-faint hover:bg-hover hover:text-ink lg:hidden"
          aria-label="Menú"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          {/* El título de la pantalla lo pone su propio <h1>: repetirlo acá era decir dos
              veces lo mismo a 40px de distancia, compitiendo con él. Queda lo que sólo se
              sabe desde esta barra — de cuándo son los datos que estás mirando. */}
          <p className="truncate text-xs font-medium text-ink-muted">
            {dash.data ? (
              <>
                Datos del B2B al <span className="font-semibold text-ink-strong">{dash.data.asOf}</span>
              </>
            ) : (
              'Cargando…'
            )}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        <button
          onClick={alternar}
          className="relative grid h-9 w-9 place-items-center rounded-lg text-ink-muted hover:bg-hover hover:text-ink-strong"
          aria-label={tema === 'claro' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}
          title={tema === 'claro' ? 'Modo oscuro' : 'Modo claro'}
        >
          <Sun
            className={cn(
              'absolute h-4 w-4 transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)]',
              tema === 'claro' ? 'scale-100 opacity-100 blur-0' : 'scale-[0.25] opacity-0 blur-[4px]',
            )}
          />
          <Moon
            className={cn(
              'absolute h-4 w-4 transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)]',
              tema === 'oscuro' ? 'scale-100 opacity-100 blur-0' : 'scale-[0.25] opacity-0 blur-[4px]',
            )}
          />
        </button>
        <button
          onClick={() => qc.invalidateQueries()}
          className="grid h-9 w-9 place-items-center rounded-lg text-ink-muted hover:bg-hover hover:text-ink-strong"
          aria-label="Actualizar"
          title="Actualizar datos"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
        <button
          className="relative grid h-9 w-9 place-items-center rounded-lg text-ink-muted hover:bg-hover hover:text-ink-strong"
          aria-label="Notificaciones"
        >
          <Bell className="h-4 w-4" />
          {/* El punto rojo estaba fijo: avisaba de alertas incluso con cero. */}
          {alertas > 0 && (
            <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-danger ring-2 ring-canvas" />
          )}
        </button>
        <div className="mx-1 h-6 w-px bg-hairline" />
        {/* Antes decía "Carlos Ruiz · Jefe de Compras" fijo. Ahora es quien está logueado. */}
        <div className="relative">
          <button
            onClick={() => setMenu((m) => !m)}
            className="flex items-center gap-2.5 rounded-lg py-1 pl-1 pr-2 hover:bg-hover"
          >
            <span className="grid h-8 w-8 place-items-center rounded-full bg-primary text-2xs font-bold text-white">
              {iniciales(user?.name)}
            </span>
            <span className="hidden text-left leading-tight sm:block">
              <span className="block max-w-[11rem] truncate text-xs font-semibold text-ink-strong">
                {user?.name ?? '—'}
              </span>
              <span className="block text-2xs capitalize text-ink-muted">{user?.role ?? ''}</span>
            </span>
            <ChevronDown className="hidden h-4 w-4 text-ink-faint sm:block" />
          </button>

          {menu && (
            <>
              {/* Capa para cerrar el menú al hacer click afuera. */}
              <div className="fixed inset-0 z-30" onClick={() => setMenu(false)} />
              <div className="absolute right-0 z-40 mt-2 w-56 overflow-hidden rounded-xl border border-hairline bg-panel shadow-lg">
                <div className="border-b border-hairline px-4 py-3">
                  <p className="truncate text-xs font-semibold text-ink-strong">{user?.name}</p>
                  <p className="mt-0.5 truncate font-mono text-2xs text-ink-faint">{user?.email}</p>
                </div>
                <button
                  onClick={() => logout.mutate()}
                  disabled={logout.isPending}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-xs font-medium text-ink hover:bg-hover disabled:opacity-50"
                >
                  <LogOut className="h-3.5 w-3.5 shrink-0" />
                  {logout.isPending ? 'Saliendo…' : 'Cerrar sesión'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
